import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook for managing conversation mode state and operations
 * Handles mode changes, loading states, and success/error messaging
 */
const useModeManagement = (initialMode = 'ai') => {
  const [currentMode, setCurrentMode] = useState(initialMode);
  const [modeChangeLoading, setModeChangeLoading] = useState(false);
  const [modeChangeError, setModeChangeError] = useState('');
  const [modeChangeSuccess, setModeChangeSuccess] = useState('');

  // Handle mode change with API call
  const handleSetMode = useCallback(async (newMode, conversationId, token) => {
    if (!conversationId || modeChangeLoading || newMode === currentMode) return;
    
    setModeChangeLoading(true);
    setModeChangeError('');
    setModeChangeSuccess('');
    
    try {
      const response = await api.setConversationMode(conversationId, newMode, token);
      setCurrentMode(response.mode);
      setModeChangeSuccess(response.msg || `Mode changed to ${newMode}`);
      return response;
    } catch (err) {
      console.error('Error setting conversation mode:', err);
      setModeChangeError(err.message || 'Failed to change conversation mode');
      throw err;
    } finally {
      setModeChangeLoading(false);
    }
  }, [currentMode, modeChangeLoading]);

  // Update mode without API call (for socket updates)
  const updateMode = useCallback((newMode) => {
    setCurrentMode(newMode);
  }, []);

  // Clear success message
  const clearModeChangeSuccess = useCallback(() => {
    setModeChangeSuccess('');
  }, []);

  // Clear error message
  const clearModeChangeError = useCallback(() => {
    setModeChangeError('');
  }, []);

  // Clear all messages
  const clearModeChangeMessages = useCallback(() => {
    setModeChangeSuccess('');
    setModeChangeError('');
  }, []);

  // Reset mode state
  const resetModeState = useCallback((newMode = 'ai') => {
    setCurrentMode(newMode);
    setModeChangeLoading(false);
    setModeChangeError('');
    setModeChangeSuccess('');
  }, []);

  return {
    currentMode,
    modeChangeLoading,
    modeChangeError,
    modeChangeSuccess,
    handleSetMode,
    updateMode,
    clearModeChangeSuccess,
    clearModeChangeError,
    clearModeChangeMessages,
    resetModeState,
    setCurrentMode
  };
};

export default useModeManagement;